/**
 * The operator's view: every account, its devices, connections, modules and
 * recent activity; disable and re-enable accounts; revoke anything; manage the
 * admin roster and the access list that decides who may use the deployment at
 * all; decide who may invite people, and publish the extension build they are
 * sent to. All but the first few are super-admin work, and the controls are
 * absent — not merely disabled — for an ordinary admin, so the page never
 * offers a button the Worker would refuse.
 *
 * What is deliberately absent: any way to read a user's captured data. Capture
 * logs live in the user's extension and are never sent here, so there is
 * nothing on this page that could show them — which is the honest reason, and
 * the one worth keeping true.
 */

import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Nav from "react-bootstrap/Nav";
import Offcanvas from "react-bootstrap/Offcanvas";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import type { AccessRuleAdded, AccessSettings, AdminRole, AdminStats, AdminUserSummary, Feature } from "@xtract-bot/embellisher-schema";
import { api, errorMessage } from "../api";
import { AllInvitations, SendOutcome } from "../components/invitations";
import { ShareFeatureModal, shareOutcomeMessage } from "../components/shares";
import {
  ConfirmModal,
  EmptyState,
  ErrorAlert,
  InlineSpinner,
  Skeleton,
  SkeletonRows,
  StatusDot,
  TimeAgo,
  formatAbsolute,
  useConfirm,
} from "../components/ui";
import { useAsync } from "../useAsync";
import { useSession } from "../useSession";

/* ── Stat tiles ───────────────────────────────────────────────────────────── */

const TILES: Array<{ key: keyof AdminStats; label: string; hint: string }> = [
  { key: "accounts", label: "Accounts", hint: "every account that has signed in" },
  { key: "disabledAccounts", label: "Disabled", hint: "refused at sign-in and on every token" },
  { key: "devices", label: "Linked browsers", hint: "links that have not been revoked" },
  { key: "onlineDevices", label: "Online now", hint: "sockets held by the 50 most recent accounts" },
  { key: "connections", label: "Assistant connections", hint: "live MCP authorizations" },
  { key: "bundles", label: "Configurations", hint: "accounts with a saved bundle" },
  { key: "logicModules", label: "Logic modules", hint: "compiled modules in storage" },
  { key: "mcpCallsLast24h", label: "Tool calls · 24h", hint: "recorded MCP calls across the deployment" },
];

function Stats(): ReactNode {
  const stats = useAsync(() => api.adminStats(), []);
  if (stats.error) return <ErrorAlert error={stats.error} onRetry={() => void stats.reload()} />;
  return (
    <Row className="g-3 mb-4">
      {TILES.map((tile) => (
        <Col key={tile.key} xs={6} md={4} xl={3}>
          <Card className="h-100">
            <Card.Body className="py-3">
              <div className="emb-muted small">{tile.label}</div>
              <div className="fs-3 fw-semibold lh-1 my-1">
                {stats.loading || !stats.data ? <Skeleton width="3.2rem" height="1.9rem" /> : stats.data[tile.key].toLocaleString()}
              </div>
              <div className="emb-muted" style={{ fontSize: "0.76rem" }}>
                {tile.hint}
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

/* ── User detail ──────────────────────────────────────────────────────────── */

function UserDetail({ accountId, onClose, onChanged }: { accountId: string; onClose: () => void; onChanged: () => void }): ReactNode {
  const { account: viewer } = useSession();
  const detail = useAsync((signal) => api.adminUser(accountId), [accountId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sharing, setSharing] = useState<Feature | null>(null);
  const [shared, setShared] = useState<{ text: string; warning: boolean } | null>(null);
  const disable = useConfirm<{ accountId: string; username: string; disable: boolean }>();
  const revokeDevice = useConfirm<{ deviceId: string; name: string }>();
  const revokeConnection = useConfirm<{ authId: string; clientName: string }>();

  const after = useCallback(async () => {
    await detail.reload();
    onChanged();
  }, [detail, onChanged]);

  const account = detail.data?.account;

  return (
    <Offcanvas show placement="end" onHide={onClose} style={{ width: "min(46rem, 100%)" }}>
      <Offcanvas.Header closeButton>
        <div>
          <Offcanvas.Title as="h2" className="h5 mb-0">
            {account?.username ?? accountId}
          </Offcanvas.Title>
          <div className="emb-muted small emb-mono">{accountId}</div>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {detail.loading ? (
          <SkeletonRows rows={8} />
        ) : detail.error ? (
          <ErrorAlert error={detail.error} onRetry={() => void detail.reload()} />
        ) : !detail.data || !account ? null : (
          <>
            {error ? (
              <Alert variant="danger" onClose={() => setError(null)} dismissible>
                {error}
              </Alert>
            ) : null}

            <div className="emb-sunken p-3 mb-4">
              <Row className="g-2 small">
                <Col sm={6}>
                  <span className="emb-muted">Email</span>
                  <div>{account.email ?? "none verified"}</div>
                </Col>
                <Col sm={6}>
                  <span className="emb-muted">Provider</span>
                  <div>{account.provider}</div>
                </Col>
                <Col sm={6}>
                  <span className="emb-muted">Joined</span>
                  <div>{formatAbsolute(account.createdAt)}</div>
                </Col>
                <Col sm={6}>
                  <span className="emb-muted">Last seen</span>
                  <div>
                    <TimeAgo iso={account.lastSeenAt} />
                  </div>
                </Col>
                <Col sm={6}>
                  <span className="emb-muted">Configuration</span>
                  <div>
                    v{account.bundleVersion} · {account.featureCount} features
                  </div>
                </Col>
                <Col sm={6}>
                  <span className="emb-muted">Status</span>
                  <div>
                    {account.disabledAt ? <Badge bg="danger">disabled</Badge> : <Badge bg="success">active</Badge>}
                    {account.isAdmin ? <Badge bg="primary" className="ms-2">admin</Badge> : null}
                    {account.canInvite ? <Badge bg="info" className="ms-2">can invite</Badge> : null}
                  </div>
                </Col>
              </Row>
              <InviteSwitch
                account={account}
                canEdit={viewer?.isSuperAdmin === true}
                onError={setError}
                onChanged={() => void after()}
              />
              <div className="mt-3">
                <Button
                  size="sm"
                  variant={account.disabledAt ? "outline-success" : "outline-danger"}
                  disabled={busy}
                  onClick={() => disable.ask({ accountId, username: account.username, disable: account.disabledAt === null })}
                >
                  {account.disabledAt ? "Re-enable account" : "Disable account"}
                </Button>
              </div>
            </div>

            <h3 className="h6">Linked browsers</h3>
            {detail.data.devices.length === 0 ? (
              <p className="emb-muted small">None.</p>
            ) : (
              <Table size="sm" responsive className="align-middle mb-4">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Last seen</th>
                    <th>Extension</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {detail.data.devices.map((device) => (
                    <tr key={device.deviceId} className={device.revokedAt ? "opacity-50" : ""}>
                      <td>{device.name}</td>
                      <td>{device.revokedAt ? <span className="emb-muted">revoked</span> : <StatusDot online={device.online} />}</td>
                      <td>
                        <TimeAgo iso={device.lastSeenAt} />
                      </td>
                      <td className="emb-mono">{device.extensionVersion ?? "—"}</td>
                      <td className="text-end">
                        {device.revokedAt ? null : (
                          <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => revokeDevice.ask({ deviceId: device.deviceId, name: device.name })}>
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            <h3 className="h6">Features</h3>
            {detail.data.features === null ? (
              <p className="emb-muted small mb-4">
                A super-admin can see this account's features here, to pass one on to someone else. Ordinary admins
                cannot: there is nothing they could do with it.
              </p>
            ) : detail.data.features.length === 0 ? (
              <p className="emb-muted small mb-4">None configured.</p>
            ) : (
              <>
                {shared ? (
                  <Alert variant={shared.warning ? "warning" : "success"} className="py-2 small" onClose={() => setShared(null)} dismissible>
                    {shared.text}
                  </Alert>
                ) : null}
                <ul className="list-unstyled mb-4 small">
                  {detail.data.features.map((feature) => (
                    <li key={feature.id} className="d-flex flex-wrap align-items-baseline gap-2 py-1 border-bottom border-light-subtle">
                      <span className="fw-semibold">{feature.name}</span>
                      <code className="emb-mono emb-muted">{feature.id}</code>
                      {feature.logic ? (
                        <Badge bg="warning" text="dark">
                          logic
                        </Badge>
                      ) : null}
                      <span className="emb-muted ms-auto">{feature.match.hosts.join(", ")}</span>
                      <Button size="sm" variant="link" className="p-0" onClick={() => setSharing(feature)}>
                        Share
                      </Button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <h3 className="h6">Assistant connections</h3>
            {detail.data.connections.length === 0 ? (
              <p className="emb-muted small">None.</p>
            ) : (
              <Table size="sm" responsive className="align-middle mb-4">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Approved</th>
                    <th>Last used</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {detail.data.connections.map((entry) => (
                    <tr key={entry.authId} className={entry.revokedAt ? "opacity-50" : ""}>
                      <td>{entry.clientName}</td>
                      <td>
                        <TimeAgo iso={entry.createdAt} />
                      </td>
                      <td>
                        <TimeAgo iso={entry.lastUsedAt} />
                      </td>
                      <td className="text-end">
                        {entry.revokedAt ? (
                          <span className="emb-muted small">revoked</span>
                        ) : (
                          <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => revokeConnection.ask({ authId: entry.authId, clientName: entry.clientName })}>
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            <h3 className="h6">Logic modules</h3>
            {detail.data.logicModules.length === 0 ? (
              <p className="emb-muted small">None.</p>
            ) : (
              <ul className="list-unstyled mb-4 small">
                {detail.data.logicModules.map((module) => (
                  <li key={module.moduleId} className="d-flex justify-content-between gap-2 py-1 border-bottom border-light-subtle">
                    <span className="emb-mono">
                      {module.moduleId} <span className="emb-muted">v{module.version}</span>
                    </span>
                    <span className="emb-muted">
                      {(module.sizeBytes / 1024).toFixed(1)} kB · <TimeAgo iso={module.createdAt} />
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <h3 className="h6">Recent activity</h3>
            {detail.data.recentActivity.length === 0 ? (
              <p className="emb-muted small mb-0">No tool calls recorded.</p>
            ) : (
              <div>
                {detail.data.recentActivity.map((event) => (
                  <div className="emb-activity-row" key={event.id}>
                    <span className="emb-tool-name">{event.tool}</span>
                    <span className={event.ok ? "small" : "small text-danger"}>{event.summary}</span>
                    <span className="emb-muted small text-nowrap">
                      <TimeAgo iso={event.at} />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Offcanvas.Body>

      <ShareFeatureModal
        feature={sharing}
        onBehalfOf={account?.username ?? accountId}
        send={(featureId, toEmail, note) => api.adminShareFeature(accountId, featureId, toEmail, note)}
        onClose={() => setSharing(null)}
        onShared={(share) => setShared(shareOutcomeMessage(share))}
      />

      <ConfirmModal
        show={disable.subject !== null}
        title={disable.subject?.disable ? "Disable this account?" : "Re-enable this account?"}
        confirmLabel={disable.subject?.disable ? "Disable" : "Re-enable"}
        variant={disable.subject?.disable ? "danger" : "primary"}
        busy={disable.busy}
        error={disable.error}
        onCancel={disable.cancel}
        onConfirm={() =>
          void disable.run(async (subject) => {
            setBusy(true);
            try {
              await api.adminSetDisabled(subject.accountId, subject.disable);
              await after();
            } finally {
              setBusy(false);
            }
          })
        }
      >
        {disable.subject?.disable ? (
          <p className="mb-0">
            <strong>{disable.subject.username}</strong> will be refused at sign-in, on every device token and on every
            MCP call, from the next request. Nothing is deleted, and re-enabling restores it exactly.
          </p>
        ) : (
          <p className="mb-0">
            <strong>{disable.subject?.username}</strong> will be able to sign in again. Their existing browsers and
            assistant connections start working again too — revoke any you do not want back first.
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        show={revokeDevice.subject !== null}
        title="Revoke this browser's link?"
        confirmLabel="Revoke link"
        busy={revokeDevice.busy}
        error={revokeDevice.error}
        onCancel={revokeDevice.cancel}
        onConfirm={() =>
          void revokeDevice.run(async (subject) => {
            await api.adminRevokeDevice(accountId, subject.deviceId);
            await after();
          })
        }
      >
        <p className="mb-0">
          <strong>{revokeDevice.subject?.name}</strong> is disconnected on its next request; the user has to link it
          again from the extension.
        </p>
      </ConfirmModal>

      <ConfirmModal
        show={revokeConnection.subject !== null}
        title="Revoke this connection?"
        confirmLabel="Revoke connection"
        busy={revokeConnection.busy}
        error={revokeConnection.error}
        onCancel={revokeConnection.cancel}
        onConfirm={() =>
          void revokeConnection.run(async (subject) => {
            await api.adminRevokeConnection(accountId, subject.authId);
            await after();
          })
        }
      >
        <p className="mb-0">
          <strong>{revokeConnection.subject?.clientName}</strong> stops working immediately. The user can approve it
          again from their own client.
        </p>
      </ConfirmModal>
    </Offcanvas>
  );
}

/**
 * The one control on a user that decides whether they may invite people.
 *
 * A super-admin may always invite, so for one of them the switch would change
 * nothing they could see — it says so instead of offering it. An account with
 * no verified address cannot be granted it at all, because the grant is by
 * address.
 */
function InviteSwitch({
  account,
  canEdit,
  onError,
  onChanged,
}: {
  account: AdminUserSummary;
  canEdit: boolean;
  onError: (message: string) => void;
  onChanged: () => void;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const email = account.email;

  const toggle = useCallback(async () => {
    if (!email) return;
    setBusy(true);
    try {
      if (account.isInviter) await api.adminRemoveInviter(email);
      else await api.adminAddInviter(email);
      onChanged();
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }, [account.isInviter, email, onChanged, onError]);

  return (
    <div className="mt-3 d-flex flex-wrap align-items-center gap-3 small">
      {canEdit && email && !account.isSuperAdmin ? (
        <Form.Check
          type="switch"
          id={`emb-invite-${account.accountId}`}
          checked={account.isInviter}
          disabled={busy}
          onChange={() => void toggle()}
          label="May invite people"
        />
      ) : (
        <span className="emb-muted">
          {account.isSuperAdmin
            ? "Invites as a super-admin — no separate grant needed."
            : !email
              ? "Cannot be allowed to invite: no verified email address."
              : account.isInviter
                ? "May invite people (a super-admin granted it)."
                : "May not invite people. A super-admin can allow it."}
        </span>
      )}
      {busy ? <InlineSpinner label="Saving" /> : null}
    </div>
  );
}

/* ── Users ────────────────────────────────────────────────────────────────── */

function Users(): ReactNode {
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const users = useAsync(() => api.adminUsers(search ? { q: search } : {}), [search]);

  useEffect(() => {
    // Debounced so a search is one request per pause, not one per keystroke.
    const timer = window.setTimeout(() => setSearch(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const rows: AdminUserSummary[] = users.data?.users ?? [];

  return (
    <>
      <Card>
        <Card.Header className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
          <span>Accounts</span>
          <div className="d-flex align-items-center gap-3">
            {users.reloading ? <InlineSpinner label="Searching" /> : null}
            <Form.Control
              type="search"
              size="sm"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search username, email or id"
              aria-label="Search accounts"
              style={{ width: "16rem" }}
            />
          </div>
        </Card.Header>
        <Card.Body className={users.loading || users.error || rows.length === 0 ? "" : "p-0"}>
          {users.loading ? (
            <SkeletonRows rows={6} />
          ) : users.error ? (
            <ErrorAlert error={users.error} onRetry={() => void users.reload()} />
          ) : rows.length === 0 ? (
            <EmptyState title={search ? `Nothing matches “${search}”` : "No accounts yet"}>
              {search ? "Try part of a username, an email address, or an account id." : "An account appears here the first time someone signs in."}
            </EmptyState>
          ) : (
            <Table responsive hover className="align-middle">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Browsers</th>
                  <th>Connections</th>
                  <th>Config</th>
                  <th>Last seen</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => (
                  <tr key={user.accountId} role="button" onClick={() => setSelected(user.accountId)}>
                    <td>
                      <div className="fw-semibold d-flex align-items-center gap-2">
                        {user.username}
                        {user.isAdmin ? <Badge bg="primary">admin</Badge> : null}
                        {user.canInvite ? <Badge bg="info">can invite</Badge> : null}
                        {user.disabledAt ? <Badge bg="danger">disabled</Badge> : null}
                      </div>
                      <div className="emb-muted small">{user.email ?? "no verified email"}</div>
                    </td>
                    <td>{user.deviceCount}</td>
                    <td>{user.connectionCount}</td>
                    <td className="emb-mono">
                      v{user.bundleVersion} <span className="emb-muted">· {user.featureCount}</span>
                    </td>
                    <td>
                      <TimeAgo iso={user.lastSeenAt} />
                    </td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-secondary" onClick={(event) => { event.stopPropagation(); setSelected(user.accountId); }}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {users.data?.nextCursor ? (
          <Card.Footer className="emb-muted small">
            More accounts exist than are shown. Narrow the list with the search box.
          </Card.Footer>
        ) : null}
      </Card>

      {selected ? <UserDetail accountId={selected} onClose={() => setSelected(null)} onChanged={() => void users.reload()} /> : null}
    </>
  );
}

/* ── Admins ───────────────────────────────────────────────────────────────── */

function RoleBadge({ role }: { role: AdminRole }): ReactNode {
  return role === "super" ? <Badge bg="primary">super-admin</Badge> : <Badge bg="secondary">admin</Badge>;
}

function Admins({ canEdit }: { canEdit: boolean }): ReactNode {
  const admins = useAsync(() => api.adminAdmins(), []);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AdminRole>("admin");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = useConfirm<string>();
  const changeRole = useConfirm<{ email: string; role: AdminRole }>();

  const add = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!email.trim()) return;
      setAdding(true);
      setError(null);
      try {
        await api.adminAddAdmin(email.trim(), role);
        setEmail("");
        setRole("admin");
        await admins.reload();
      } catch (cause) {
        setError(errorMessage(cause));
      } finally {
        setAdding(false);
      }
    },
    [email, role, admins],
  );

  return (
    <Row className="g-4">
      <Col lg={7}>
        <Card>
          <Card.Header>Administrators</Card.Header>
          <Card.Body className={admins.loading || admins.error ? "" : "p-0"}>
            {admins.loading ? (
              <SkeletonRows rows={3} />
            ) : admins.error ? (
              <ErrorAlert error={admins.error} onRetry={() => void admins.reload()} />
            ) : (
              <Table responsive hover className="align-middle">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Added by</th>
                    <th>Added</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {admins.data?.map((entry) => (
                    <tr key={entry.email}>
                      <td>
                        <span className="emb-mono">{entry.email}</span>
                        {entry.bootstrap ? (
                          <Badge bg="secondary" className="ms-2">
                            from configuration
                          </Badge>
                        ) : null}
                      </td>
                      <td>
                        <RoleBadge role={entry.role} />
                      </td>
                      <td className="emb-muted">{entry.addedBy ?? "—"}</td>
                      <td>{entry.bootstrap && entry.addedBy === null ? <span className="emb-muted">—</span> : <TimeAgo iso={entry.addedAt} />}</td>
                      <td className="text-end">
                        {entry.bootstrap ? (
                          <span className="emb-muted small">locked</span>
                        ) : canEdit ? (
                          <div className="d-flex gap-3 justify-content-end">
                            <Button
                              size="sm"
                              variant="link"
                              className="p-0"
                              onClick={() => changeRole.ask({ email: entry.email, role: entry.role === "super" ? "admin" : "super" })}
                            >
                              {entry.role === "super" ? "Make admin" : "Make super-admin"}
                            </Button>
                            <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => remove.ask(entry.email)}>
                              Remove
                            </Button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col lg={5}>
        {canEdit ? (
          <Card className="mb-4">
            <Card.Body>
              <h2 className="h6 mb-3">Add an administrator</h2>
              <Form onSubmit={(event) => void add(event)}>
                <Form.Group className="mb-3" controlId="emb-admin-email">
                  <Form.Label className="small emb-muted">Verified email address</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="someone@example.com"
                    autoComplete="off"
                  />
                  <Form.Text className="emb-muted">
                    Authorises nobody on its own — the holder must still prove this address at a sign-in provider, and
                    the address must be on the access list to sign in at all.
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3" controlId="emb-admin-role">
                  <Form.Label className="small emb-muted">Role</Form.Label>
                  <Form.Select size="sm" value={role} onChange={(event) => setRole(event.target.value === "super" ? "super" : "admin")}>
                    <option value="admin">Admin — accounts, devices, connections</option>
                    <option value="super">Super-admin — also the roster and the access list</option>
                  </Form.Select>
                </Form.Group>
                {error ? (
                  <Alert variant="danger" className="py-2 small">
                    {error}
                  </Alert>
                ) : null}
                <Button type="submit" variant="primary" size="sm" disabled={adding || email.trim().length === 0}>
                  {adding ? "Adding…" : "Add administrator"}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        ) : (
          <Card className="mb-4">
            <Card.Body>
              <h2 className="h6 mb-2">Changing this roster needs a super-admin</h2>
              <p className="emb-muted small mb-0">
                You can see who administers this deployment, which is what answers “who do I ask?”. Adding, removing and
                promoting are kept to super-admins: an admin who could edit this roster could grant themselves the
                access list, and then neither guards anything.
              </p>
            </Card.Body>
          </Card>
        )}

        <Card>
          <Card.Body>
            <h2 className="h6 mb-2">Why some entries are locked</h2>
            <p className="emb-muted small mb-0">
              Addresses marked <em>from configuration</em> come from this deployment's <code>ADMIN_BOOTSTRAP</code>{" "}
              setting. They are always super-admins and always allowed past the access list, and they cannot be removed
              or demoted here on purpose: a roster that can lock every operator out is a roster somebody eventually
              locks every operator out of. Change the setting and redeploy instead.
            </p>
          </Card.Body>
        </Card>
      </Col>

      <ConfirmModal
        show={remove.subject !== null}
        title="Remove this administrator?"
        confirmLabel="Remove"
        busy={remove.busy}
        error={remove.error}
        onCancel={remove.cancel}
        onConfirm={() =>
          void remove.run(async (subject) => {
            await api.adminRemoveAdmin(subject);
            await admins.reload();
          })
        }
      >
        <p className="mb-0">
          <span className="emb-mono">{remove.subject}</span> loses admin access on their next request — the roster is
          read fresh every time, so it does not wait for a cookie to expire. They keep their own account, and stay on
          the access list if they are on it.
        </p>
      </ConfirmModal>

      <ConfirmModal
        show={changeRole.subject !== null}
        title={changeRole.subject?.role === "super" ? "Make this a super-admin?" : "Reduce this to an ordinary admin?"}
        confirmLabel={changeRole.subject?.role === "super" ? "Make super-admin" : "Make admin"}
        variant={changeRole.subject?.role === "super" ? "primary" : "danger"}
        busy={changeRole.busy}
        error={changeRole.error}
        onCancel={changeRole.cancel}
        onConfirm={() =>
          void changeRole.run(async (subject) => {
            await api.adminSetAdminRole(subject.email, subject.role);
            await admins.reload();
          })
        }
      >
        {changeRole.subject?.role === "super" ? (
          <p className="mb-0">
            <span className="emb-mono">{changeRole.subject.email}</span> will be able to change this roster and the
            access list — which means deciding who may use this deployment, and who else may decide that.
          </p>
        ) : (
          <p className="mb-0">
            <span className="emb-mono">{changeRole.subject?.email}</span> keeps every other admin power and loses the
            roster and the access list, from their next request.
          </p>
        )}
      </ConfirmModal>
    </Row>
  );
}

/* ── Access ───────────────────────────────────────────────────────────────── */

/**
 * Who may use this deployment at all.
 *
 * The order on the page is the order of the decision: the switch, then what it
 * allows, then who it shuts out. That last list is the one that matters —
 * turning enforcement on without seeing who it refuses is how an operator
 * locks out a colleague and hears about it a day later — so it is shown
 * whether enforcement is on or off, and the confirm names the count.
 */
function Access({ canEdit }: { canEdit: boolean }): ReactNode {
  const access = useAsync(() => api.adminAccess(), []);
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<AccessRuleAdded | null>(null);
  // A domain gets no email, so the line-to-them field is only offered for an
  // address. The same reading `parseRule` gives on the Worker: a leading `@`,
  // or no `@` at all, is a domain.
  const trimmed = value.trim();
  const isDomain = trimmed.length > 0 && (trimmed.startsWith("@") || !trimmed.includes("@"));
  const removeRule = useConfirm<string>();
  const setPolicy = useConfirm<{ enforcing: boolean; refused: number; atLeast: boolean }>();

  const settings: AccessSettings | null = access.data ?? null;

  const add = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!value.trim()) return;
      setAdding(true);
      setError(null);
      setAdded(null);
      try {
        setAdded(
          await api.adminAddAccessRule(value.trim(), {
            ...(note.trim() ? { note: note.trim() } : {}),
            ...(message.trim() && !isDomain ? { message: message.trim() } : {}),
          }),
        );
        setValue("");
        setNote("");
        setMessage("");
        await access.reload();
      } catch (cause) {
        setError(errorMessage(cause));
      } finally {
        setAdding(false);
      }
    },
    [value, note, message, isDomain, access],
  );

  if (access.loading) return <SkeletonRows rows={8} />;
  if (access.error) return <ErrorAlert error={access.error} onRetry={() => void access.reload()} />;
  if (!settings) return null;

  return (
    <Row className="g-4">
      <Col lg={7}>
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex flex-wrap gap-3 justify-content-between align-items-start">
              <div>
                <h2 className="h6 mb-1">
                  {settings.enforcing ? "The access list is being enforced" : "The access list is not being enforced"}
                </h2>
                <p className="emb-muted small mb-0">
                  {settings.enforcing
                    ? "Only the addresses below may sign in, hold a linked browser, or answer an assistant. Checked on every request."
                    : "The list is kept but not applied: anyone who can sign in with a provider may use this deployment."}
                </p>
              </div>
              {canEdit ? (
                <Form.Check
                  type="switch"
                  id="emb-access-enforcing"
                  className="fs-5"
                  checked={settings.enforcing}
                  onChange={() =>
                    setPolicy.ask({
                      enforcing: !settings.enforcing,
                      refused: settings.refused.length,
                      atLeast: settings.refusedTruncated,
                    })
                  }
                  aria-label="Enforce the access list"
                />
              ) : null}
            </div>
            {settings.updatedAt ? (
              <p className="emb-muted small mb-0 mt-3">
                Last changed <TimeAgo iso={settings.updatedAt} /> {settings.updatedBy ? `by ${settings.updatedBy}` : null}
              </p>
            ) : null}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>Authorised addresses</Card.Header>
          <Card.Body className={settings.rules.length === 0 ? "" : "p-0"}>
            {settings.rules.length === 0 ? (
              <EmptyState title="Nothing on the list">
                {canEdit
                  ? "Add an address or a domain. Until then, enforcing this list would refuse everyone but the addresses from configuration."
                  : "A super-admin adds addresses here."}
              </EmptyState>
            ) : (
              <Table responsive hover className="align-middle">
                <thead>
                  <tr>
                    <th>Address or domain</th>
                    <th>Why</th>
                    <th>Added</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {settings.rules.map((rule) => (
                    <tr key={rule.value}>
                      <td>
                        <span className="emb-mono">{rule.value}</span>
                        {rule.kind === "domain" ? (
                          <Badge bg="secondary" className="ms-2">
                            domain
                          </Badge>
                        ) : null}
                      </td>
                      <td className="emb-muted small">{rule.note ?? "—"}</td>
                      <td className="small">
                        <TimeAgo iso={rule.addedAt} />
                        {rule.addedBy ? <div className="emb-muted">{rule.addedBy}</div> : null}
                      </td>
                      <td className="text-end">
                        {canEdit ? (
                          <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => removeRule.ask(rule.value)}>
                            Remove
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        <Card {...(settings.refused.length > 0 ? { border: "warning" } : {})}>
          <Card.Header>
            {settings.enforcing ? "Accounts this list refuses" : "Accounts this list would refuse"}
            {settings.refused.length > 0 ? <Badge bg="warning" text="dark" className="ms-2">{settings.refused.length}</Badge> : null}
          </Card.Header>
          <Card.Body className={settings.refused.length === 0 ? "" : "p-0"}>
            {settings.refused.length === 0 ? (
              <p className="emb-muted small mb-0">
                {settings.refusedTruncated
                  ? "None of the accounts checked match nothing on the list. This deployment has more accounts than are checked here, so this is not the whole answer."
                  : "Every account that has signed in matches a rule. Enforcing the list locks nobody out."}
              </p>
            ) : (
              <Table responsive className="align-middle mb-0">
                <tbody>
                  {settings.refused.map((account) => (
                    <tr key={account.accountId}>
                      <td>
                        <div className="fw-semibold">{account.username}</div>
                        <div className="emb-muted small">{account.email ?? "no verified email address"}</div>
                      </td>
                      <td className="text-end">
                        {canEdit && account.email ? (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => {
                              setValue(account.email ?? "");
                              setNote("");
                            }}
                          >
                            Authorise
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
          {settings.refusedTruncated ? (
            <Card.Footer className="emb-muted small">
            More are refused than are listed here — this is at least {settings.refused.length}, not exactly.
          </Card.Footer>
          ) : null}
        </Card>
      </Col>

      <Col lg={5}>
        {canEdit ? (
          <Card className="mb-4">
            <Card.Body>
              <h2 className="h6 mb-3">Authorise an address</h2>
              <Form onSubmit={(event) => void add(event)}>
                <Form.Group className="mb-3" controlId="emb-access-value">
                  <Form.Label className="small emb-muted">Email address or domain</Form.Label>
                  <Form.Control
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                    placeholder="someone@example.com or @example.com"
                    autoComplete="off"
                  />
                  <Form.Text className="emb-muted">
                    A domain written with its <code>@</code> matches every address at it, and under it —{" "}
                    <code>@example.com</code> also admits <code>you@mail.example.com</code>.
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3" controlId="emb-access-note">
                  <Form.Label className="small emb-muted">Why (optional, for this list only)</Form.Label>
                  <Form.Control value={note} onChange={(event) => setNote(event.target.value)} placeholder="Contractor, until March" />
                </Form.Group>
                {isDomain ? null : (
                  <Form.Group className="mb-3" controlId="emb-access-message">
                    <Form.Label className="small emb-muted">A line to them in the email (optional)</Form.Label>
                    <Form.Control
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      placeholder="Welcome aboard — this is the tool I mentioned"
                      maxLength={1000}
                    />
                  </Form.Group>
                )}
                <p className="emb-muted small">
                  {isDomain
                    ? "A domain sends no email — there is no one address to write to. Send people there the install page yourself."
                    : "Authorising an address emails them setup instructions: sign in with this address, download the extension, load it into Chrome, link it and connect their assistant. It shows under Invitations, from you."}
                </p>
                {error ? (
                  <Alert variant="danger" className="py-2 small">
                    {error}
                  </Alert>
                ) : null}
                <Button type="submit" variant="primary" size="sm" disabled={adding || value.trim().length === 0}>
                  {adding ? "Adding…" : isDomain ? "Authorise" : "Authorise and email"}
                </Button>
              </Form>
              {added ? (
                <div className="mt-3">
                  {added.invitation ? (
                    <SendOutcome result={added.invitation} onClose={() => setAdded(null)} />
                  ) : (
                    <Alert variant="info" className="small mb-0" onClose={() => setAdded(null)} dismissible>
                      <span className="emb-mono">{added.value}</span> is authorised. {added.invitationSkipped}
                    </Alert>
                  )}
                </div>
              ) : null}
            </Card.Body>
          </Card>
        ) : (
          <Card className="mb-4">
            <Card.Body>
              <h2 className="h6 mb-2">Changing this list needs a super-admin</h2>
              <p className="emb-muted small mb-0">
                You can read it, which is what answers “why can this person not sign in?”. Editing it is kept to
                super-admins.
              </p>
            </Card.Body>
          </Card>
        )}

        <Card className="mb-4">
          <Card.Body>
            <h2 className="h6 mb-2">Always allowed</h2>
            <p className="emb-muted small mb-2">
              These come from this deployment's <code>ADMIN_BOOTSTRAP</code> setting. They are super-admins and are
              allowed past this list whether or not they appear on it, so a mistake here cannot lock every operator out.
            </p>
            <ul className="mb-0 small emb-mono">
              {settings.alwaysAllowed.map((email) => (
                <li key={email}>{email}</li>
              ))}
            </ul>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <h2 className="h6 mb-2">What enforcing actually stops</h2>
            <p className="emb-muted small mb-0">
              An address that does not match is refused at sign-in, on the extension's device token, on the hub socket
              and on every MCP call — checked per request, so adding or removing an address takes effect on the next
              one rather than when a credential expires. An account whose provider never confirmed an email address
              cannot match any rule, because there is nothing to match.
            </p>
          </Card.Body>
        </Card>
      </Col>

      <ConfirmModal
        show={removeRule.subject !== null}
        title="Remove this from the access list?"
        confirmLabel="Remove"
        variant="danger"
        busy={removeRule.busy}
        error={removeRule.error}
        onCancel={removeRule.cancel}
        onConfirm={() =>
          void removeRule.run(async (subject) => {
            await api.adminRemoveAccessRule(subject);
            await access.reload();
          })
        }
      >
        <p className="mb-0">
          {settings.enforcing ? (
            <>
              Anyone admitted only by <span className="emb-mono">{removeRule.subject}</span> is locked out on their very
              next request — signed-in browser, linked extension and assistant connection alike. Nothing is deleted.
            </>
          ) : (
            <>
              <span className="emb-mono">{removeRule.subject}</span> comes off the list. Nothing changes today, because
              the list is not being enforced.
            </>
          )}
        </p>
      </ConfirmModal>

      <ConfirmModal
        show={setPolicy.subject !== null}
        title={setPolicy.subject?.enforcing ? "Enforce the access list?" : "Stop enforcing the access list?"}
        confirmLabel={setPolicy.subject?.enforcing ? "Enforce it" : "Stop enforcing"}
        variant={setPolicy.subject?.enforcing ? "primary" : "danger"}
        busy={setPolicy.busy}
        error={setPolicy.error}
        onCancel={setPolicy.cancel}
        onConfirm={() =>
          void setPolicy.run(async (subject) => {
            await api.adminSetAccessPolicy(subject.enforcing);
            await access.reload();
          })
        }
      >
        {setPolicy.subject?.enforcing ? (
          <p className="mb-0">
            From the next request, only the addresses on this list may use this deployment.{" "}
            {setPolicy.subject.refused > 0 ? (
              <strong>
                {setPolicy.subject.atLeast ? "At least " : ""}
                {setPolicy.subject.refused} account{setPolicy.subject.refused === 1 ? "" : "s"} that already signed in
                match nothing on it and will be locked out.
              </strong>
            ) : setPolicy.subject.atLeast ? (
              "No account among those checked is locked out by it, but this deployment has more accounts than are checked."
            ) : (
              "No account that has signed in is locked out by it."
            )}
          </p>
        ) : (
          <p className="mb-0">
            Anyone who can sign in with one of this deployment's providers will be able to use it, whether or not they
            are on the list. The list is kept, so enforcing it again restores exactly this.
          </p>
        )}
      </ConfirmModal>
    </Row>
  );
}

/* ── Invitations ──────────────────────────────────────────────────────────── */

/** Who may invite people, by verified address. */
function Inviters({ canEdit }: { canEdit: boolean }): ReactNode {
  const inviters = useAsync(() => api.adminInviters(), []);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = useConfirm<string>();

  const add = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!email.trim()) return;
      setAdding(true);
      setError(null);
      try {
        await api.adminAddInviter(email.trim());
        setEmail("");
        await inviters.reload();
      } catch (cause) {
        setError(errorMessage(cause));
      } finally {
        setAdding(false);
      }
    },
    [email, inviters],
  );

  return (
    <Card className="mb-4">
      <Card.Header>Who may invite people</Card.Header>
      <Card.Body>
        <p className="emb-muted small">
          An invitation puts the address on the access list and emails them how to install the extension, so this is a
          slice of the access list handed to someone else. Super-admins may always invite.
        </p>
        {inviters.loading ? (
          <SkeletonRows rows={2} />
        ) : inviters.error ? (
          <ErrorAlert error={inviters.error} onRetry={() => void inviters.reload()} />
        ) : !inviters.data || inviters.data.length === 0 ? (
          <p className="small mb-3">Nobody besides the super-admins.</p>
        ) : (
          <ul className="list-unstyled small mb-3">
            {inviters.data.map((entry) => (
              <li key={entry.email} className="d-flex align-items-baseline gap-2 py-1 border-bottom border-light-subtle">
                <span className="emb-mono">{entry.email}</span>
                <span className="emb-muted ms-auto text-nowrap">
                  <TimeAgo iso={entry.addedAt} />
                </span>
                {canEdit ? (
                  <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => remove.ask(entry.email)}>
                    Remove
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canEdit ? (
          <Form onSubmit={(event) => void add(event)}>
            <Form.Label className="small emb-muted" htmlFor="emb-inviter-email">
              Allow an address to invite
            </Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                id="emb-inviter-email"
                type="email"
                size="sm"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="someone@example.com"
                autoComplete="off"
              />
              <Button type="submit" size="sm" variant="primary" disabled={adding || email.trim().length === 0}>
                {adding ? "Adding…" : "Allow"}
              </Button>
            </div>
            <Form.Text className="emb-muted">Or use the switch on an account under Accounts.</Form.Text>
            {error ? (
              <Alert variant="danger" className="py-2 small mt-2 mb-0">
                {error}
              </Alert>
            ) : null}
          </Form>
        ) : (
          <p className="emb-muted small mb-0">A super-admin decides who may invite.</p>
        )}
      </Card.Body>

      <ConfirmModal
        show={remove.subject !== null}
        title="Stop this address inviting people?"
        confirmLabel="Remove"
        busy={remove.busy}
        error={remove.error}
        onCancel={remove.cancel}
        onConfirm={() =>
          void remove.run(async (subject) => {
            await api.adminRemoveInviter(subject);
            await inviters.reload();
          })
        }
      >
        <p className="mb-0">
          <span className="emb-mono">{remove.subject}</span> can no longer invite or resend, from their next request.
          The people they already invited keep their access — that is on the access list, where you can remove it.
        </p>
      </ConfirmModal>
    </Card>
  );
}

/** `embellisher-1.2.3.zip` → `1.2.3`, so the version field fills itself in. */
function versionFromFilename(name: string): string {
  return /(\d+\.\d+\.\d+)/.exec(name)?.[1] ?? "";
}

/** The build invited people download, and the upload that replaces it. */
function ExtensionBuilds({ canEdit }: { canEdit: boolean }): ReactNode {
  const builds = useAsync(() => api.adminExtensions(), []);
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const remove = useConfirm<string>();

  const publish = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!file || !version.trim()) return;
      setUploading(true);
      setError(null);
      try {
        await api.adminPublishExtension(version.trim(), file);
        setFile(null);
        setVersion("");
        setInputKey((key) => key + 1);
        await builds.reload();
      } catch (cause) {
        setError(errorMessage(cause));
      } finally {
        setUploading(false);
      }
    },
    [file, version, builds],
  );

  const latest = builds.data?.[0] ?? null;

  return (
    <Card>
      <Card.Header>The extension build</Card.Header>
      <Card.Body>
        <p className="emb-muted small">
          Invited people download the newest build here, from <code>/install</code>, once they have signed in. It is the
          full channel — <code>embellisher-&lt;version&gt;.zip</code>, not <code>-store</code> or <code>-shell</code>.
        </p>
        {builds.loading ? (
          <SkeletonRows rows={2} />
        ) : builds.error ? (
          <ErrorAlert error={builds.error} onRetry={() => void builds.reload()} />
        ) : !latest ? (
          <Alert variant="warning" className="py-2 small">
            Nothing is published yet, so <code>/install</code> has nothing to offer and invitations say so.
          </Alert>
        ) : (
          <ul className="list-unstyled small mb-3">
            {builds.data?.map((build, index) => (
              <li key={build.version} className="d-flex align-items-baseline gap-2 py-1 border-bottom border-light-subtle">
                <span className="emb-mono">v{build.version}</span>
                {index === 0 ? <Badge bg="success">offered</Badge> : null}
                <span className="emb-muted ms-auto text-nowrap">
                  {(build.sizeBytes / 1024).toFixed(0)} kB · <TimeAgo iso={build.uploadedAt} />
                </span>
                {canEdit ? (
                  <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => remove.ask(build.version)}>
                    Delete
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canEdit ? (
          <Form onSubmit={(event) => void publish(event)}>
            <Form.Group className="mb-2" controlId="emb-extension-file">
              <Form.Label className="small emb-muted">Publish a build</Form.Label>
              <Form.Control
                key={inputKey}
                type="file"
                size="sm"
                accept=".zip,application/zip"
                onChange={(event) => {
                  const chosen = (event.target as HTMLInputElement).files?.[0] ?? null;
                  setFile(chosen);
                  if (chosen) setVersion(versionFromFilename(chosen.name));
                }}
              />
            </Form.Group>
            <div className="d-flex gap-2">
              <Form.Control size="sm" value={version} onChange={(event) => setVersion(event.target.value)} placeholder="1.2.3" aria-label="Version" style={{ maxWidth: "8rem" }} />
              <Button type="submit" size="sm" variant="primary" disabled={uploading || !file || version.trim().length === 0}>
                {uploading ? "Publishing…" : "Publish"}
              </Button>
            </div>
            <Form.Text className="emb-muted">
              The Worker checks the zip's manifest says this version and that it is the full build before storing it. From
              a terminal: <code>pnpm --filter @xtract-bot/embellisher-extension extension:publish --wrangler</code>.
            </Form.Text>
            {error ? (
              <Alert variant="danger" className="py-2 small mt-2 mb-0">
                {error}
              </Alert>
            ) : null}
          </Form>
        ) : (
          <p className="emb-muted small mb-0">A super-admin publishes builds.</p>
        )}
      </Card.Body>

      <ConfirmModal
        show={remove.subject !== null}
        title={`Delete build v${remove.subject ?? ""}?`}
        confirmLabel="Delete"
        variant="danger"
        busy={remove.busy}
        error={remove.error}
        onCancel={remove.cancel}
        onConfirm={() =>
          void remove.run(async (subject) => {
            await api.adminDeleteExtension(subject);
            await builds.reload();
          })
        }
      >
        <p className="mb-0">
          {remove.subject === latest?.version
            ? "This is the build /install offers now; the next newest takes its place, or nothing if it is the only one. Extensions already installed from it keep working."
            : "Nobody is offered this build any more. Extensions already installed from it keep working."}
        </p>
      </ConfirmModal>
    </Card>
  );
}

function Invitations({ canEdit }: { canEdit: boolean }): ReactNode {
  return (
    <Row className="g-4">
      <Col lg={7}>
        <AllInvitations canWithdraw={canEdit} />
      </Col>
      <Col lg={5}>
        <Inviters canEdit={canEdit} />
        <ExtensionBuilds canEdit={canEdit} />
      </Col>
    </Row>
  );
}

/* ── The page ─────────────────────────────────────────────────────────────── */

type AdminTab = "users" | "admins" | "access" | "invitations";

function isAdminTab(value: string | null): value is AdminTab {
  return value === "users" || value === "admins" || value === "access" || value === "invitations";
}

export function Admin(): ReactNode {
  const { account } = useSession();
  const [tab, setTab] = useState<AdminTab>("users");
  // Absent for an ordinary admin rather than disabled: a control the Worker
  // would refuse is worse than no control.
  const canEdit = account?.isSuperAdmin === true;

  if (account && !account.isAdmin) {
    return (
      <Container style={{ maxWidth: "40rem" }}>
        <Card>
          <Card.Body className="p-4">
            <h1 className="h5 mb-2">This page is for administrators</h1>
            <p className="emb-muted mb-0">
              Administration is by verified email address. If you expected to be an administrator here, the address you
              proved at your sign-in provider is not on this deployment's roster — usually because the provider would
              not confirm an address at all.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: "74rem" }}>
      <p className="emb-eyebrow mb-2">Admin</p>
      <h1 className="h3 mb-4">Deployment</h1>
      <Stats />

      <Nav variant="tabs" activeKey={tab} onSelect={(key) => setTab(isAdminTab(key) ? key : "users")} className="mb-4">
        <Nav.Item>
          <Nav.Link eventKey="users">Accounts</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="access">Access</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="invitations">Invitations</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="admins">Administrators</Nav.Link>
        </Nav.Item>
      </Nav>

      {tab === "users" ? (
        <Users />
      ) : tab === "access" ? (
        <Access canEdit={canEdit} />
      ) : tab === "invitations" ? (
        <Invitations canEdit={canEdit} />
      ) : (
        <Admins canEdit={canEdit} />
      )}
    </Container>
  );
}
